import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid2";
import {
  Chip,
  Collapse,
  IconButton,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AdvisorGapRule } from "../helpers/advisorStaticData";
import "../Advisor.css";

interface Props {
  gapRulesData: AdvisorGapRule[];
  onEditRule: (gapRule: AdvisorGapRule) => void;
  onDuplicateRule: (gapRule: AdvisorGapRule) => void;
}

type SortDirection = "asc" | "desc";

// minWidths stop the browser collapsing the two long-text columns down to a
// few characters per line once the other columns have taken their share
const tableHeaders = [
  {
    id: 0,
    KEY: "rule_title",
    label: "Rule Title",
    sortable: true,
    minWidth: 220,
  },
  { id: 1, KEY: "expand", label: "", sortable: false, minWidth: 40 },
  { id: 2, KEY: "id", label: "ID", sortable: true, minWidth: 60 },
  {
    id: 3,
    KEY: "target_field",
    label: "Target field",
    sortable: true,
    minWidth: 120,
  },
  {
    id: 4,
    KEY: "expression_sql",
    label: "Expression",
    sortable: true,
    minWidth: 240,
  },
  {
    id: 5,
    KEY: "destination_table",
    label: "Destination Table",
    sortable: true,
    minWidth: 160,
  },
  {
    id: 6,
    KEY: "maps_using",
    label: "Maps Using Gap Rule",
    sortable: true,
    minWidth: 150,
  },
  { id: 7, KEY: "is_active", label: "Status", sortable: true, minWidth: 90 },
  { id: 8, KEY: "actions", label: "Actions", sortable: false, minWidth: 90 },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 100];

// shared styling for the expanded detail panel - a small grey caption above
// each value, with an optional hint below it
const detailLabelSx = { fontSize: "12px", color: "text.secondary" };
const detailValueSx = { fontSize: "14px", overflowWrap: "anywhere" as const };
const detailHelperSx = { fontSize: "11px", color: "text.disabled", mt: 0.25 };

const AdvisorGapRulesTable = ({
  gapRulesData,
  onEditRule,
  onDuplicateRule,
}: Props) => {
  // the Maps tab shares this route, so the params are namespaced
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  // without this, rules can be viewed but none of their actions are offered
  const hasPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "businesse_advisor",
    "create_edit_delete_advisor_maps_&_gap_rules",
  );
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: SortDirection;
  }>({ keytoSort: "", direction: "asc" });
  const [expandedRuleId, setExpandedRuleId] = React.useState<number | null>(
    null,
  );

  const rowsPerPageFromUrl = parseInt(
    searchParams.get("gapRulesRowsPerPage") ?? "",
    10,
  );
  const tableRowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(rowsPerPageFromUrl)
    ? rowsPerPageFromUrl
    : 10;

  const rowCount = gapRulesData?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(rowCount / tableRowsPerPage));
  const pageFromUrl = parseInt(searchParams.get("gapRulesPage") ?? "", 10);
  const currentPage =
    pageFromUrl >= 1 && pageFromUrl <= totalPages ? pageFromUrl : 1;
  const tablePage = currentPage - 1;

  // keep the URL honest when the filters shrink the list under the current page
  React.useEffect(() => {
    if (pageFromUrl === currentPage) return;
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("gapRulesPage", String(currentPage));
        return params;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFromUrl, currentPage]);

  const handleChangePage = (_: any, newPage: number) => {
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("gapRulesPage", String(newPage + 1));
        params.set("gapRulesRowsPerPage", String(tableRowsPerPage));
        return params;
      },
      { replace: true },
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);
        params.set("gapRulesRowsPerPage", event.target.value);
        params.set("gapRulesPage", "1");
        return params;
      },
      { replace: true },
    );
  };

  const handleHeaderClick = (header: (typeof tableHeaders)[number]) => {
    if (!header.sortable) return;
    setSort((prev) => ({
      keytoSort: header.KEY,
      direction:
        prev.keytoSort === header.KEY && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const toggleExpandedRule = (ruleId: number) =>
    setExpandedRuleId((prev) => (prev === ruleId ? null : ruleId));

  const sortedRows = React.useMemo(() => {
    if (!sort.keytoSort) return gapRulesData ?? [];
    return (gapRulesData ?? []).slice().sort((a: any, b: any) => {
      const aValue = a[sort.keytoSort];
      const bValue = b[sort.keytoSort];
      if (aValue === undefined || aValue === null || aValue === "") return 1;
      if (bValue === undefined || bValue === null || bValue === "") return -1;
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return sort.direction === "asc"
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    });
  }, [gapRulesData, sort]);

  const paginatedRows = sortedRows.slice(
    tablePage * tableRowsPerPage,
    tablePage * tableRowsPerPage + tableRowsPerPage,
  );

  return (
    <TableContainer className="advisorGapRulesTable">
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {tableHeaders.map((header) => (
              <TableCell
                key={header.id}
                align="left"
                sx={{ minWidth: header.minWidth }}
              >
                <TableSortLabel
                  active={sort.keytoSort === header.KEY}
                  direction={
                    sort.keytoSort === header.KEY ? sort.direction : "asc"
                  }
                  onClick={() => handleHeaderClick(header)}
                  disabled={!header.sortable}
                >
                  {header.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows && paginatedRows.length > 0 ? (
            paginatedRows.map((row) => {
              const isExpanded = expandedRuleId === row.id;

              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom: isExpanded
                          ? "none !important"
                          : "1px solid rgba(224, 224, 224, 1) !important",
                        padding: "10px 16px",
                      },
                    }}
                  >
                    <TableCell align="left" sx={{ maxWidth: "280px" }}>
                      <span
                        data-testid="advisor-gap-rule-title-link"
                        data-tracker="954"
                        style={{
                          color: "#9C27B0",
                          cursor: "pointer",
                          overflowWrap: "anywhere",
                        }}
                        onClick={() => toggleExpandedRule(row.id)}
                      >
                        {row.rule_title}
                      </span>
                    </TableCell>
                    <TableCell align="left">
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        color="secondary"
                        onClick={() => toggleExpandedRule(row.id)}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell align="left">{row.id}</TableCell>
                    <TableCell align="left">{row.target_field}</TableCell>
                    <TableCell align="left">{row.expression_sql}</TableCell>
                    <TableCell align="left">{row.destination_table}</TableCell>
                    <TableCell align="left">{row.maps_using}</TableCell>
                    <TableCell align="left">
                      <Chip
                        size="small"
                        variant="outlined"
                        color={row.is_active ? "success" : "default"}
                        icon={
                          row.is_active ? (
                            <CheckCircleOutlineIcon fontSize="small" />
                          ) : (
                            <HighlightOffIcon fontSize="small" />
                          )
                        }
                        label={row.is_active ? "Active" : "Inactive"}
                        sx={{
                          backgroundColor: row.is_active
                            ? "rgba(46, 125, 50, 0.08)"
                            : "rgba(0, 0, 0, 0.06)",
                        }}
                      />
                    </TableCell>
                    <TableCell align="left">
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Tooltip title="Edit" placement="top">
                          <IconButton
                            sx={{ width: "36px", height: "36px" }}
                            onClick={() => onEditRule(row)}
                            data-tracker="957"
                            disabled={!hasPermission}
                          >
                            <EditIcon color="secondary" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate" placement="top">
                          <IconButton
                            sx={{ width: "36px", height: "36px" }}
                            onClick={() => onDuplicateRule(row)}
                            data-tracker="959"
                            disabled={!hasPermission}
                          >
                            <ContentCopyIcon
                              color="secondary"
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      className="advisorGapRulesDetailCell"
                      style={{ padding: 0, border: "none" }}
                      colSpan={tableHeaders.length}
                    >
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            padding: "12px 16px 20px",
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          }}
                        >
                          <Typography sx={detailLabelSx}>
                            Simple English
                          </Typography>
                          <Typography sx={{ fontSize: "15px", mb: 2.5 }}>
                            {row.description || "-"}
                          </Typography>

                          <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography sx={detailLabelSx}>
                                Expression (JSON op-tree)
                              </Typography>
                              <Typography sx={detailValueSx}>
                                {row.expression
                                  ? JSON.stringify(row.expression)
                                  : "-"}
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography sx={detailLabelSx}>
                                Depends On
                              </Typography>
                              <Typography sx={detailValueSx}>
                                {row.dependency_field || "-"}
                              </Typography>
                              <Typography sx={detailHelperSx}>
                                Metrics only(comma-separated)
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 3 }}>
                              <Typography sx={detailLabelSx}>
                                Data Source
                              </Typography>
                              <Typography sx={detailValueSx}>
                                {row.data_source || "-"}
                              </Typography>
                              <Typography sx={detailHelperSx}>
                                Optional
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={tableHeaders.length} align="center">
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box sx={{ flex: 1 }}>
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={rowCount}
            rowsPerPage={tableRowsPerPage}
            page={tablePage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              // the spacer is "flex: 1 1 100%" and would push the label, select
              // and count across to the right - drop it so they stay bottom-left
              ".MuiTablePagination-spacer": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
                justifyContent: "flex-start",
              },
              ".MuiTablePagination-displayedRows": {
                marginLeft: "24px",
              },
            }}
          />
        </Box>
        <Pagination
          count={totalPages}
          page={currentPage}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          onChange={(_, value) => handleChangePage(null, value - 1)}
        />
        {/* spacer keeps the pagination visually centered under the table */}
        <Box sx={{ flex: 1 }} />
      </Stack>
    </TableContainer>
  );
};

export default AdvisorGapRulesTable;
